/**
 * Contact tracking — "Did you send it? Mark as contacted" hybrid flow.
 *
 * Coaches click an email link → Gmail opens in a new tab → this hook also
 * surfaces a small banner in the MaxSC tab asking them to confirm. One tap
 * logs the contact event to the backend with timestamp + coach attribution.
 *
 * Two surfaces:
 *   • leads (user accounts) — POST /api/coach/leads/:user_id/log-contact
 *   • assessment requests   — POST /api/coach/assessment-requests/:req_id/log-contact
 *
 * Usage:
 *   const contact = useContactTracker();
 *   <a onClick={() => contact.prompt({type:"lead", id:lead.user_id, name:lead.name})}>
 *
 *   {contact.pending && <ContactConfirmBanner tracker={contact} />}
 */
import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useContactTracker() {
    const [pending, setPending] = useState(null);
    const [busy, setBusy] = useState(false);
    const [lastConfirmed, setLastConfirmed] = useState(null);
    // { type, id, at } — flips on every successful confirm so callers can refresh

    const prompt = useCallback((target) => {
        if (!target?.id) return;
        setPending(target);
    }, []);

    const dismiss = useCallback(() => setPending(null), []);

    const confirm = useCallback(async (note = "") => {
        if (!pending) return null;
        setBusy(true);
        try {
            const path = pending.type === "request"
                ? `/coach/assessment-requests/${pending.id}/log-contact`
                : `/coach/leads/${pending.id}/log-contact`;
            const r = await axios.post(
                `${API}${path}`,
                { method: "email", note },
                { withCredentials: true },
            );
            toast.success(`Marked ${pending.name || "contact"} as contacted`);
            const finished = { ...pending, at: Date.now() };
            setLastConfirmed(finished);
            setPending(null);
            return r.data;
        } catch (e) {
            toast.error("Couldn't log contact", { description: e?.response?.data?.detail });
            return null;
        } finally {
            setBusy(false);
        }
    }, [pending]);

    return { pending, busy, prompt, dismiss, confirm, lastConfirmed };
}

/**
 * Human-friendly relative time. Returns "just now", "3h ago", "yesterday", "5d ago".
 * Used by lead detail panels.
 */
export function lastContactedLabel(iso) {
    if (!iso) return null;
    const then = new Date(iso);
    const now = new Date();
    const diffMin = Math.round((now - then) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay === 1) return "yesterday";
    if (diffDay < 7) return `${diffDay}d ago`;
    return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Returns true if the last contact was more than `days` ago — used to nudge
 * the coach to follow up.
 */
export function isContactStale(iso, days = 7) {
    if (!iso) return false;
    const diffDays = (Date.now() - new Date(iso).getTime()) / 86400000;
    return diffDays > days;
}
